import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Container } from "./ui/Container";
import { Footer } from "./Footer";
import { Button } from "./ui/Button";
import { trackTryKnoothClick } from "../lib/analytics";
import { guideArticles, getGuideNav } from "../lib/guideData";
import { cn } from "../lib/utils";

/* ── Sidebar icon components ─────────────────────────────────────────── */
const GuideIcon = ({ icon, className = "" }) => {
  const base = `w-5 h-5 flex-shrink-0 ${className}`;
  switch (icon) {
    case "rocket":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.63 8.41m5.96 5.96a14.926 14.926 0 01-5.84 2.58m0 0a6 6 0 01-7.38-5.84h4.8" />
        </svg>
      );
    case "record":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
        </svg>
      );
    case "timeline":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-3.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-1.5A1.125 1.125 0 0 1 18 18.375M20.625 4.5H3.375m17.25 0c.621 0 1.125.504 1.125 1.125M20.625 4.5h-1.5C18.504 4.5 18 5.004 18 5.625m3.75 0v1.5c0 .621-.504 1.125-1.125 1.125M3.375 4.5c-.621 0-1.125.504-1.125 1.125M3.375 4.5h1.5C5.496 4.5 6 5.004 6 5.625m-3.75 0v1.5c0 .621.504 1.125 1.125 1.125m0 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m1.5-3.75C5.496 8.25 6 7.746 6 7.125v-1.5M4.875 8.25C5.496 8.25 6 8.754 6 9.375v1.5m0-5.25v5.25m0-5.25C6 5.004 6.504 4.5 7.125 4.5h9.75c.621 0 1.125.504 1.125 1.125m1.125 2.625h1.5m-1.5 0A1.125 1.125 0 0 1 18 7.125v-1.5m1.125 2.625c-.621 0-1.125.504-1.125 1.125v1.5m2.625-2.625c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125M18 5.625v5.25M7.125 12h9.75m-9.75 0A1.125 1.125 0 0 1 6 10.875M7.125 12C6.504 12 6 12.504 6 13.125m0-2.25v2.25m0 0v1.5c0 .621.504 1.125 1.125 1.125M18 10.875c0 .621-.504 1.125-1.125 1.125M18 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m-12 5.25v-5.25m0 5.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125m-12 0v-1.5c0-.621-.504-1.125-1.125-1.125M18 18.375v-5.25m0 5.25v-1.5c0-.621.504-1.125 1.125-1.125M18 13.125v1.5c0 .621.504 1.125 1.125 1.125M18 13.125c0-.621.504-1.125 1.125-1.125M6 13.125v1.5c0 .621-.504 1.125-1.125 1.125M6 13.125C6 12.504 5.496 12 4.875 12m-1.5 0h1.5m-1.5 0c-.621 0-1.125-.504-1.125-1.125v-1.5c0-.621.504-1.125 1.125-1.125m1.5 3.75C5.496 12 6 11.496 6 10.875v-1.5" />
        </svg>
      );
    case "animation":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
        </svg>
      );
    case "zoom":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM10.5 7.5v6m3-3h-6" />
        </svg>
      );
    case "cursor":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672ZM12 2.25V4.5m5.834.166-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243-1.59-1.59" />
        </svg>
      );
    case "palette":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.098 19.902a3.75 3.75 0 0 0 5.304 0l6.401-6.402M6.75 21A3.75 3.75 0 0 1 3 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 0 0 3.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008Z" />
        </svg>
      );
    case "audio":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
        </svg>
      );
    case "privacy":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
        </svg>
      );
    case "export":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
        </svg>
      );
    default:
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
        </svg>
      );
  }
};

/**
 * Shared layout for guide pages: sidebar navigation + content area.
 * Modeled after Screen Studio's guide layout.
 */
const GuideLayout = ({ slug, children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { prev, next } = getGuideNav(slug);

  /* Close sidebar on route change */
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  /* Lock body scroll when mobile sidebar is open */
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  return (
    <div id="top" className="min-h-screen bg-[#FBFBFD]">
      <main className="pt-[100px] md:pt-[120px]">
        <Container>
          <div className="max-w-[1200px] mx-auto">
            {/* ── Mobile sidebar toggle ─────────────────────────── */}
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="lg:hidden mb-6 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-black/10 text-[14px] font-medium text-[#1D1D1F] hover:bg-black/[0.02] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
              Guide Navigation
            </button>

            <div className="flex gap-10">
              {/* ── Sidebar ──────────────────────────────────────── */}
              {/* Desktop sidebar */}
              <aside className="hidden lg:block w-[260px] flex-shrink-0">
                <div className="sticky top-[120px]">
                  <SidebarContent slug={slug} />
                </div>
              </aside>

              {/* Mobile sidebar overlay */}
              {sidebarOpen && (
                <div className="fixed inset-0 z-[90] lg:hidden">
                  <div
                    className="absolute inset-0 bg-black/20 backdrop-blur-sm"
                    onClick={() => setSidebarOpen(false)}
                  />
                  <div className="absolute left-0 top-0 bottom-0 w-[300px] bg-[#FBFBFD] border-r border-black/10 p-6 pt-[120px] overflow-y-auto">
                    <SidebarContent slug={slug} onItemClick={() => setSidebarOpen(false)} />
                  </div>
                </div>
              )}

              {/* ── Content area ──────────────────────────────────── */}
              <div className="flex-1 min-w-0">
                {/* ── breadcrumb ───────────────────────────────────── */}
                <div className="flex items-center gap-2 text-[14px] text-[#666666] mb-8">
                  <Link to="/" className="hover:text-[#1D1D1F] transition-colors">
                    Home
                  </Link>
                  <span>/</span>
                  <Link to="/guide" className="hover:text-[#1D1D1F] transition-colors">
                    Guide
                  </Link>
                  {slug && (
                    <>
                      <span>/</span>
                      <span className="text-[#1D1D1F] font-medium truncate max-w-[260px]">
                        {guideArticles.find((a) => a.slug === slug)?.title}
                      </span>
                    </>
                  )}
                </div>

                <article className="max-w-[800px] blog-article guide-article">
                  {children}
                </article>

                {/* ── Prev / Next navigation ───────────────────── */}
                <div className="mt-16 mb-8 flex flex-col sm:flex-row gap-4 max-w-[800px]">
                  {prev && (
                    <Link
                      to={`/guide/${prev.slug}`}
                      className={`${next ? 'flex-1' : 'w-full'} group rounded-2xl border border-black/5 bg-white p-5 hover:border-black/10 hover:shadow-sm transition-all`}
                    >
                      <span className="text-[12px] font-semibold tracking-[0.08em] uppercase text-[#86868B]">
                        ← Previous
                      </span>
                      <p className="mt-1.5 text-[15px] font-semibold text-[#1D1D1F] group-hover:text-[#2B00FF] transition-colors">
                        {prev.title}
                      </p>
                    </Link>
                  )}
                  {next && (
                    <Link
                      to={`/guide/${next.slug}`}
                      className={`${prev ? 'flex-1' : 'w-full'} group rounded-2xl border border-black/5 bg-white p-5 hover:border-black/10 hover:shadow-sm transition-all ${prev ? 'text-right' : ''}`}
                    >
                      <span className="text-[12px] font-semibold tracking-[0.08em] uppercase text-[#86868B]">
                        Next →
                      </span>
                      <p className="mt-1.5 text-[15px] font-semibold text-[#1D1D1F] group-hover:text-[#2B00FF] transition-colors">
                        {next.title}
                      </p>
                    </Link>
                  )}
                </div>

                {/* ── CTA ─────────────────────────────────────── */}
                <div className="mt-[80px] mb-[100px] text-center max-w-[800px]">
                  <h2
                    className="text-3xl md:text-4xl lg:text-[48px] font-[900] tracking-[-0.02em] leading-none text-transparent bg-clip-text"
                    style={{
                      backgroundImage:
                        "linear-gradient(180deg, #31220D 57%, #666666 100%)",
                    }}
                  >
                    Ready to record?
                  </h2>
                  <div className="mt-[32px] flex flex-col items-center gap-[10px]">
                    <Button
                      href="https://apps.apple.com/app/knooth/id6756659300?mt=12"
                      target="_blank"
                      rel="noreferrer"
                      data-analytics="try-knooth"
                      data-location="guide-cta"
                      onClick={() => trackTryKnoothClick("guide-cta")}
                      className="w-[176px] h-[44px] px-6 py-[10px] rounded-full text-white font-bold text-[24px] hover:shadow-lg transition-all border-none leading-none flex items-center justify-center"
                      style={{
                        background:
                          "linear-gradient(180deg, #2F00EC 57%, #C8A5FF 100%)",
                      }}
                    >
                      Try knooth
                    </Button>
                    <p className="text-[14px] font-normal leading-[1.4] text-[#31220D]">
                      1 week free for new subscribers.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </main>

      <Footer />
    </div>
  );
};

/* ── Sidebar content (shared between desktop & mobile) ────────────────── */
const SidebarContent = ({ slug, onItemClick }) => (
  <nav className="space-y-1">
    <h3 className="text-[12px] font-semibold tracking-[0.08em] uppercase text-[#86868B] mb-4 px-3">
      Guide
    </h3>
    {guideArticles.map((article) => {
      const isActive = article.slug === slug;
      return (
        <Link
          key={article.slug}
          to={`/guide/${article.slug}`}
          onClick={onItemClick}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-xl text-[14px] font-medium transition-all",
            isActive
              ? "bg-[#2B00FF]/10 text-[#2B00FF]"
              : "text-[#1D1D1F]/70 hover:text-[#1D1D1F] hover:bg-black/[0.03]"
          )}
        >
          <GuideIcon
            icon={article.icon}
            className={isActive ? "text-[#2B00FF]" : "text-[#86868B]"}
          />
          <span className="truncate">{article.title}</span>
        </Link>
      );
    })}
  </nav>
);

export { GuideLayout, GuideIcon };
